// table.jsx
import React, { forwardRef, useImperativeHandle, useRef } from "react";
import Table from "react-bootstrap/Table";
import styles from "./plantio-colheita.module.css";
import { useTheme } from "@mui/material";
import { tokens } from "../../../theme";

import beans from "../../../utils/assets/icons/beans2.png";
import soy from "../../../utils/assets/icons/soy.png";
import rice from "../../../utils/assets/icons/rice.png";
import cotton from "../../../utils/assets/icons/cotton.png";
import question from "../../../utils/assets/icons/question.png";
import corn from "../../../utils/assets/icons/corn.png";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { Tooltip } from "@mui/material";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";


const TableColheita = forwardRef(({ data, idsPending }, ref) => {
	const theme = useTheme();
	const colors = tokens(theme.palette.mode);

	// ref direto no <table>
	const tableElRef = useRef(null);

	useImperativeHandle(ref, () => ({
		getPrintNode: () => tableElRef.current, // retorna o <table> real
	}));

	const iconDict = [
		{ cultura: "Feijão", icon: beans, alt: "feijao" },
		{ cultura: "FEIJAO MUNGO", icon: beans, alt: "feijao" },
		{ cultura: "Arroz", icon: rice, alt: "arroz" },
		{ cultura: "Soja", icon: soy, alt: "soja" },
		{ cultura: "Algodão", icon: cotton, alt: "algodao" },
		{ cultura: "Milho", icon: corn, alt: "milho" }
	];

	const filteredIcon = (val) => iconDict.find((d) => d.cultura === val)?.icon || question;
	const filteredAlt = (val) => iconDict.find((d) => d.cultura === val)?.alt || "unknown";

	return (
		<Table
			ref={tableElRef}
			striped
			bordered
			hover
			style={{ width: "100%", color: colors.textColor[100] }}
			size="sm"
			className={styles.mainTable}
		>
			<thead style={{ backgroundColor: colors.blueOrigin[300] }}>
				<tr>
					<th>Parcela</th>
					<th>Data Plantio</th>
					<th>Dap</th>
					<th>Cultura</th>
					<th>Variedade</th>
					<th>Area Total</th>
					<th>Area Colhida</th>
					<th>Area A Colher</th>
					<th>Peso Carregado / Scs</th>
					<th>Romaneios Computados</th>
					<th>Romaneios Pendentes</th>
					<th>Média Prev.</th>
				</tr>
			</thead>

			<tbody>
				{data.map((carga, i) => {
					const areaParcial = carga.area_parcial ? carga.area_parcial : 0;
					const areaSaldoColher = carga.area_parcial
						? carga.area_colheita - carga.area_parcial
						: carga.area_colheita;

					const formatDate = (dt) => new Date(dt).toLocaleDateString("pt-BR");

					const scsColhidos =
						carga.peso > 0
							? Number(carga.peso / 60).toLocaleString("pt-br", {
								maximumFractionDigits: 2,
								minimumFractionDigits: 2,
							})
							: " - ";

					const formatArea = (n) =>
						Number(n).toLocaleString("pt-br", {
							maximumFractionDigits: 2,
							minimumFractionDigits: 2,
						});

					const mediaPrev =
						carga.peso && carga.area_parcial ? Number(carga.peso / 60) / carga.area_parcial : 0;

					const showWarning =
						carga.peso > 0 && (!carga.area_parcial || carga.area_parcial === 0);

					const showError =
						(!carga.peso || carga.peso === 0) &&
						carga.area_parcial &&
						carga.area_parcial > 0;

					const romaneiosPending = idsPending[carga.id] ? idsPending[carga.id] : " - ";

					return (
						<tr
							key={i}
							className={`${i % 2 === 0 ? styles.oddRow : styles.evenRow} ${carga.romaneios > 0 || areaParcial > 0 || romaneiosPending > 0
								? styles.colheitaRow
								: styles.notColheitaRow
								} ${areaParcial === carga.area_colheita && romaneiosPending === " - " && i % 2 === 0 && styles.closedParcelaOdd}
              ${areaParcial === carga.area_colheita && romaneiosPending === " - " && i % 2 !== 0 && styles.closedParcelaEven}
              ${romaneiosPending > 0 && styles.romaneioIsPendindStyle}
              ${theme.palette.mode === "light" && i % 2 !== 0 && styles.oddRowLight}`}
						>
							<td>{carga.talhao__id_talhao}</td>
							<td>{formatDate(carga.data_plantio)}</td>
							<td>{carga.dap}</td>
							<td>
								<img
									src={filteredIcon(carga.variedade__cultura__cultura)}
									alt={filteredAlt(carga.variedade__cultura__cultura)}
									style={{
										filter: "drop-shadow(3px 5px 2px rgb(0 0 0 / 0.4))",
										width: "20px",
										height: "20px",
									}}
								/>
							</td>
							<td>{carga.variedade__nome_fantasia}</td>

							<td>{formatArea(carga.area_colheita)}</td>

							<td className={`${carga.romaneios > 0 && areaParcial === 0 && styles.semAreaInformada}`}>
								{areaParcial > 0 ? formatArea(areaParcial) : " - "}
							</td>

							<td>{areaSaldoColher > 0 ? formatArea(areaSaldoColher) : " - "}</td>

							<td className={`${areaParcial > 0 && carga.romaneios === 0 && styles.semAreaInformada}`}>
								{scsColhidos}
							</td>

							<td className={`${areaParcial > 0 && carga.romaneios === 0 && styles.semAreaInformada}`}>
								{carga.romaneios > 0 ? carga.romaneios : "-"}
							</td>

							<td style={{ color: romaneiosPending > 0 ? "rgb(148, 148, 55)" : colors.textColor[100] }}>
								{romaneiosPending}
							</td>

							<td
								style={{
									color: showWarning
										? "rgb(200,120,0)"
										: showError
											? "rgb(180,40,40)"
											: undefined,
									fontWeight: showWarning || showError ? 600 : undefined,
									display: "flex",
									alignItems: "center",
									textAlign: "center",
									gap: "6px",
									justifyContent: "center",
								}}
							>
								{showWarning ? (
									<Tooltip title="Existe peso carregado, mas não há área colhida informada.">
										<WarningAmberRoundedIcon
											sx={{
												fontSize: 18,
												marginTop: '3px',
												color: "rgb(200,120,0)",
											}}
										/>
									</Tooltip>
								) : showError ? (
									<Tooltip title="Existe área colhida informada, mas não há peso carregado.">
										<ErrorOutlineRoundedIcon
											sx={{
												fontSize: 18,
												marginTop: '3px',
												color: "rgb(180,40,40)",
											}}
										/>
									</Tooltip>
								) : mediaPrev > 0 ? (
									formatArea(mediaPrev)
								) : (
									"-"
								)}
							</td>
						</tr>
					);
				})}
			</tbody>
		</Table>
	);
});

export default TableColheita;
